/**
 * PieceDestroyedPopup Component
 *
 * Displays a dramatic popup when a piece is fully destroyed.
 * Shows piece type, player who lost it, and escalating flavor text.
 * Auto-dismisses after 7 seconds or can be manually dismissed.
 *
 * Props:
 * - isOpen: Boolean indicating if popup is visible
 * - pieceType: String indicating piece type (T, S, O, L, Bonus)
 * - playerWhoLost: String indicating which player lost the piece (player1 or player2)
 * - piecesLostCount: Number of pieces lost so far (used internally for flavor text intensity)
 * - totalPiecesCount: Total number of pieces in game (4 or 5 with bonus) - not displayed
 * - onDismiss: Callback when popup is dismissed
 */

import { useEffect, useRef } from 'react';
import { COPY, getPlayerLabel, getPieceIcon, selectFlavorText } from '../copy';

export default function PieceDestroyedPopup({
  isOpen = false,
  pieceType = 'T',
  playerWhoLost = 'player1',
  piecesLostCount = 0,
  totalPiecesCount = 4,
  onDismiss = null,
}) {
  const timeoutRef = useRef(null);

  // Auto-dismiss after 7 seconds
  useEffect(() => {
    if (isOpen) {
      timeoutRef.current = setTimeout(() => {
        if (onDismiss) {
          onDismiss();
        }
      }, 7000);
    }

    // Cleanup timeout on unmount or when isOpen changes
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, [isOpen, onDismiss]);

  // Handle Escape key press
  useEffect(() => {
    if (!isOpen) return;

    const handleEscape = (event) => {
      if (event.key === 'Escape' && onDismiss) {
        onDismiss();
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => {
      document.removeEventListener('keydown', handleEscape);
    };
  }, [isOpen, onDismiss]);

  // Don't render if not open
  if (!isOpen) {
    return null;
  }

  const flavorText = selectFlavorText(piecesLostCount);
  const pieceIcon = getPieceIcon(pieceType);
  const playerLabel = getPlayerLabel(playerWhoLost);

  // Determine player color styling
  const playerColorClass =
    playerWhoLost === 'player1'
      ? 'border-neon-cyan text-neon-cyan shadow-glow-cyan'
      : 'border-neon-pink text-neon-pink shadow-glow-pink';

  // Handle manual dismiss
  const handleManualDismiss = () => {
    // Clear auto-dismiss timer
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    if (onDismiss) {
      onDismiss();
    }
  };

  return (
    <div
      data-testid="popup-backdrop"
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
      role="dialog"
      aria-modal="true"
      aria-labelledby="piece-destroyed-title"
      onClick={handleManualDismiss}
    >
      <div
        data-testid="piece-destroyed-popup"
        className={`bg-dark-charcoal border-2 ${playerColorClass} rounded-lg p-6 max-w-md w-full mx-4 animate-slideInFromTop shadow-lg`}
        onClick={(event) => event.stopPropagation()} // Prevent dismiss when clicking inside popup
      >
        {/* Piece Type Icon */}
        <div className="flex items-center justify-center mb-4">
          <div
            data-testid="piece-type-icon"
            className={`text-6xl font-bold ${playerColorClass}`}
          >
            {pieceIcon}
          </div>
        </div>

        {/* Main Title */}
        <h2
          id="piece-destroyed-title"
          className={`text-2xl font-bold text-center mb-3 ${playerColorClass}`}
        >
          {COPY.pieceDestroyed.title}
        </h2>

        {/* Piece Type */}
        <p className="text-center text-xl text-gray-100 mb-4">
          {COPY.pieceDestroyed.message(playerLabel, pieceType)}
        </p>

        {/* Flavor Text */}
        <div
          data-testid="flavor-text"
          className="text-center text-xl font-bold text-neon-yellow text-glow-yellow mb-6"
        >
          {flavorText}
        </div>

        {/* Dismiss Button */}
        <div className="flex justify-center">
          <button
            onClick={handleManualDismiss}
            className={`px-6 py-2 ${playerColorClass} bg-dark-purple rounded-lg hover:bg-opacity-80 transition-all font-semibold`}
            aria-label="Close popup"
          >
            {COPY.pieceDestroyed.buttons.dismiss}
          </button>
        </div>
      </div>
    </div>
  );
}
